/**
 * Session Service
 * Handles CRUD operations for chat sessions via the backend API
 */

import { Message } from "@/components/chat/types"

// Load API URL from aws-exports.json (same API Gateway as feedback)
let SESSIONS_API_URL = ""

async function loadApiUrl(): Promise<string> {
  if (SESSIONS_API_URL) {
    return SESSIONS_API_URL
  }

  try {
    const response = await fetch("/aws-exports.json")
    const config = await response.json()
    // Sessions API shares the same API Gateway base URL as feedback
    // feedbackApiUrl ends with "/prod/" — append "sessions"
    const baseUrl = config.feedbackApiUrl
    if (!baseUrl) {
      throw new Error("API URL not found in aws-exports.json")
    }
    // feedbackApiUrl is like "https://xxx.execute-api.region.amazonaws.com/prod/"
    // We need "https://xxx.execute-api.region.amazonaws.com/prod/sessions"
    SESSIONS_API_URL = `${baseUrl.replace(/\/?$/, "/")}sessions`
    return SESSIONS_API_URL
  } catch (error) {
    console.error("Failed to load API URL from aws-exports.json:", error)
    throw new Error("Sessions API URL not configured")
  }
}

export interface SessionSummary {
  sessionId: string
  title: string
  projectId?: string
  createdAt: string
  updatedAt: string
}

export interface SessionDetail {
  sessionId: string
  title: string
  messages: Message[]
  createdAt: string
  updatedAt: string
}

export interface UpdateSessionData {
  title?: string
  messages?: Message[]
}

async function fetchWithAuth(url: string, idToken: string, options: RequestInit = {}) {
  const response = await fetch(url, {
    ...options,
    cache: "no-store",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${idToken}`,
      ...options.headers,
    },
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    const message =
      typeof errorData.error === "string"
        ? errorData.error
        : errorData.error?.message || `HTTP error! status: ${response.status}`
    const error = new Error(message) as Error & { status?: number }
    error.status = response.status
    throw error
  }

  return response.json()
}

export interface PaginatedSessionsResponse {
  sessions: SessionSummary[]
  nextCursor: string | null
}

export async function listSessions(
  idToken: string,
  projectId?: string,
  cursor?: string,
  limit: number = 20
): Promise<PaginatedSessionsResponse> {
  const apiUrl = await loadApiUrl()
  const params = new URLSearchParams()
  if (projectId) params.set("projectId", projectId)
  if (cursor) params.set("cursor", cursor)
  params.set("limit", String(limit))
  const query = params.toString()
  const url = query ? `${apiUrl}?${query}` : apiUrl
  const data = await fetchWithAuth(url, idToken)
  return {
    sessions: data.sessions || [],
    nextCursor: data.nextCursor || null,
  }
}

export interface SessionSearchResult extends SessionSummary {
  matchType: "title" | "content"
  snippet?: string
}

export interface SessionSearchResponse {
  results: SessionSearchResult[]
  contentScanned: number
  contentScanLimited: boolean
}

/** タイトル＋本文の履歴検索（2026-07-02 P3）。q は2文字以上。 */
export async function searchSessions(
  idToken: string,
  q: string
): Promise<SessionSearchResponse> {
  const apiUrl = await loadApiUrl()
  const data = await fetchWithAuth(
    `${apiUrl}/search?q=${encodeURIComponent(q)}`,
    idToken
  )
  return {
    results: data.results || [],
    contentScanned: data.contentScanned ?? 0,
    contentScanLimited: data.contentScanLimited ?? false,
  }
}

export async function createSession(
  idToken: string,
  title?: string,
  projectId?: string
): Promise<{ sessionId: string; title: string; projectId?: string; createdAt: string; updatedAt: string }> {
  const apiUrl = await loadApiUrl()
  return fetchWithAuth(apiUrl, idToken, {
    method: "POST",
    body: JSON.stringify({ title, ...(projectId ? { projectId } : {}) }),
  })
}

export async function getSession(idToken: string, sessionId: string): Promise<SessionDetail> {
  const apiUrl = await loadApiUrl()
  return fetchWithAuth(`${apiUrl}/${sessionId}`, idToken)
}

/**
 * メッセージを追記する（追記型の正規経路・2026-07-02 P2）。
 * baseCount は「ここまで保存済み」の件数。サーバ側で重複スキップ（冪等な再送）。
 * 409（クライアントがサーバより先に居る＝別端末等で欠落）はセッション再読込が必要。
 */
export async function appendMessages(
  idToken: string,
  sessionId: string,
  baseCount: number,
  messages: Message[]
): Promise<{ messageCount: number }> {
  const apiUrl = await loadApiUrl()
  return fetchWithAuth(`${apiUrl}/${sessionId}/messages`, idToken, {
    method: "POST",
    body: JSON.stringify({ baseCount, messages }),
  })
}

export async function updateSession(
  idToken: string,
  sessionId: string,
  data: UpdateSessionData
): Promise<void> {
  const apiUrl = await loadApiUrl()
  await fetchWithAuth(`${apiUrl}/${sessionId}`, idToken, {
    method: "PUT",
    body: JSON.stringify(data),
  })
}

export async function deleteSession(idToken: string, sessionId: string): Promise<void> {
  const apiUrl = await loadApiUrl()
  await fetchWithAuth(`${apiUrl}/${sessionId}`, idToken, {
    method: "DELETE",
  })
}
